import { useLayoutEffect, useEffect, useRef } from "react";
import { gsap, ScrollTrigger } from "../utils/gsap";
import ProjectCard from "../components/ProjectCard";
import projects from "../data/projects";
import { routeUrl } from "../utils/paths";

const LatestWork = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const metaNumberRef = useRef<HTMLDivElement>(null);
  const metaTitleRef = useRef<HTMLHeadingElement>(null);
  const metaSubRef = useRef<HTMLParagraphElement>(null);
  const metaListRefs = useRef<(HTMLLIElement | null)[]>([]);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const deviceRefs = useRef<(HTMLDivElement | null)[]>([]);

  const list = projects.slice(0, 3).map((project) => project.title);
  const renderMetaList = (keyPrefix: string) => (
    <ul className="latest-work__list latest-work__meta-list !hidden md:!block" aria-label="Project list">
      {list.map((name, idx) => (
        <li
          key={`${keyPrefix}-${name}-${idx}`}
          className={`latest-work__list-item${idx === 0 ? " is-active" : ""}`}
          data-index={idx}
          ref={(el) => {
            metaListRefs.current[idx] = el;
          }}
        >
          {name}
        </li>
      ))}
    </ul>
  );

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 901px)", () => {
      const ctx = gsap.context(() => {
        const frame = frameRef.current;
        const items = itemRefs.current.filter(Boolean);
        if (!frame || items.length === 0) return;

        const metaNumber = metaNumberRef.current;
        const metaTitle = metaTitleRef.current;
        const metaSub = metaSubRef.current;
        const metaListItems = metaListRefs.current.filter((item): item is HTMLLIElement => !!item);
        const prefersReduced = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        const motion = { scaleMin: 0.9, scaleMax: 1 };

        const ensureRollNode = (el: HTMLElement | null) => {
          if (!el) return null;
          const currentNodes = el.querySelectorAll(".latest-work__roll-current");
          let current: Element | null = currentNodes[0] || null;
          if (currentNodes.length > 1) {
            currentNodes.forEach((node, idx) => {
              if (idx > 0) node.remove();
            });
          }
          if (!current) {
            current = document.createElement("span");
            current.className = "latest-work__roll-current";
            current.textContent = el.textContent || "";
            el.textContent = "";
            el.appendChild(current);
          }
          return current as HTMLElement;
        };
        const setRollTextInstant = (el: HTMLElement | null, text: string) => {
          if (!el) return;
          const current = ensureRollNode(el);
          if (!current) return;
          current.textContent = text || "";
          el.querySelectorAll(".latest-work__roll-next").forEach((node) => {
            gsap.killTweensOf(node);
            node.remove();
          });
          gsap.set(current, { clearProps: "opacity,transform" });
          el.style.height = "";
        };
        const setMetaListActive = (activeIndex: number) => {
          metaListItems.forEach((item) => {
            const itemIndex = Number(item.dataset.index);
            item.classList.toggle("is-active", itemIndex === activeIndex);
          });
        };
        const formatNumber = (n: number) => (n < 10 ? `0${n}.` : `${n}.`);

        const rollText = (el: HTMLElement | null, text: string) => {
          if (!el) return;
          if (prefersReduced) {
            setRollTextInstant(el, text);
            return;
          }
          const current = ensureRollNode(el);
          if (!current) return;
          if (current.textContent === text) return;
          el.querySelectorAll(".latest-work__roll-next").forEach((node) => {
            gsap.killTweensOf(node);
            node.remove();
          });
          gsap.set(current, { opacity: 1, yPercent: 0 });

          const next = document.createElement("span");
          next.className = "latest-work__roll-next";
          next.textContent = text || "";
          el.appendChild(next);

          const currentHeight = current.offsetHeight;
          const nextHeight = next.offsetHeight;
          el.style.height = `${Math.max(currentHeight, nextHeight)}px`;

          gsap.killTweensOf([current, next]);
          gsap.set(next, {
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            yPercent: 120,
            opacity: 0,
          });
          gsap.to(current, {
            yPercent: -120,
            opacity: 0,
            duration: 0.45,
            ease: "power2.inOut",
          });
          gsap.to(next, {
            yPercent: 0,
            opacity: 1,
            duration: 0.65,
            ease: "power3.out",
            onComplete: () => {
              current.remove();
              next.classList.remove("latest-work__roll-next");
              next.classList.add("latest-work__roll-current");
              next.style.position = "";
              next.style.left = "";
              next.style.right = "";
              next.style.top = "";
              next.style.opacity = "";
              next.style.transform = "";
              el.style.height = "";
            },
          });
        };

        let lastIndex = -1;
        const updateMeta = (index: number, animate = true) => {
          if (index === lastIndex) return;
          const project = projects[index];
          if (!project) return;
          if (animate) {
            rollText(metaNumber, formatNumber(index + 1));
            rollText(metaTitle, project.title);
            rollText(metaSub, project.subtitle);
          } else {
            setRollTextInstant(metaNumber, formatNumber(index + 1));
            setRollTextInstant(metaTitle, project.title);
            setRollTextInstant(metaSub, project.subtitle);
          }
          setMetaListActive(index);
          lastIndex = index;
        };

        updateMeta(0, false);

        items.forEach((item, index) => {
          const device = deviceRefs.current[index];
          if (!device) return;
          if (prefersReduced) {
            gsap.set(device, { scale: 1 });
            return;
          }
          gsap.set(device, { scale: motion.scaleMin });
          ScrollTrigger.create({
            trigger: item,
            start: "top 65%",
            end: "bottom 35%",
            onEnter: () =>
              gsap.to(device, {
                scale: motion.scaleMax,
                duration: 0.9,
                ease: "power3.out",
                overwrite: true,
              }),
            onEnterBack: () =>
              gsap.to(device, {
                scale: motion.scaleMax,
                duration: 0.9,
                ease: "power3.out",
                overwrite: true,
              }),
            onLeave: () =>
              gsap.to(device, {
                scale: motion.scaleMin,
                duration: 0.9,
                ease: "power3.inOut",
                overwrite: true,
              }),
            onLeaveBack: () =>
              gsap.to(device, {
                scale: motion.scaleMin,
                duration: 0.9,
                ease: "power3.inOut",
                overwrite: true,
              }),
          });
        });

        items.forEach((item, index) => {
          ScrollTrigger.create({
            trigger: item,
            start: "top 60%",
            end: "bottom 40%",
            onEnter: () => updateMeta(index),
            onEnterBack: () => updateMeta(index),
          });
        });
      }, section);

      return () => ctx.revert();
    });

    mm.add("(max-width: 900px)", () => {
      const ctx = gsap.context(() => {
        const items = itemRefs.current.filter(Boolean);
        const frame = frameRef.current;
        // section is available from outer scope and confirmed non-null

        if (!frame || items.length === 0) return;

        const prefersReduced = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        if (prefersReduced) {
          items.forEach((item) => {
            gsap.set(item, { opacity: 1, y: 0, scale: 1 });
          });
          return;
        }

        // --- Mobile 3D Perspective Scroll ---

        // 1. Setup 3D Stage
        gsap.set(frame, {
          height: "65vh", // Fixed viewing window
          width: "100%",
          position: "relative",
          display: "block",
          marginTop: "auto",
          marginBottom: "auto",
          // Perspective is key for the 3D tilt effect
          perspective: 1500,
          transformStyle: "preserve-3d"
        });

        // 2. Initial State: Stacked with Deep 3D Transforms
        items.forEach((item, i) => {
          gsap.set(item, {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: items.length - i,
            backfaceVisibility: "hidden", // Performance & clean look
            transformOrigin: "center center",

            // First item is visible and flat
            autoAlpha: i === 0 ? 1 : 0,
            scale: i === 0 ? 1 : 0.85,
            rotateX: i === 0 ? 0 : -35,   // Tilted back start
            yPercent: i === 0 ? 0 : 60,   // Below view
            z: i === 0 ? 0 : -200,        // Deep in background
            filter: i === 0 ? "blur(0px)" : "blur(4px)" // Depth of field focus
          });
        });

        // 3. Create Pinned Scroll Timeline
        const totalScroll = items.length * 120; // Scroll distance

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: section,
            start: "top top",
            end: `+=${totalScroll}%`,
            pin: true,
            scrub: 1,
            anticipatePin: 1
          }
        });

        // 4. Animate the Sequence (3D Tilt / Reveal)
        items.forEach((item, i) => {
          if (i === items.length - 1) return;
          const nextItem = items[i + 1];

          tl.to(item, {
            // Exit State: Tilt forward and fly up/out
            rotateX: 35,
            yPercent: -60,
            z: -200,
            autoAlpha: 0,
            scale: 0.85,
            filter: "blur(4px)",
            duration: 1,
            ease: "power2.inOut"
          })
            .to(nextItem, {
              // Enter State: Restore from depth to flat view
              rotateX: 0,
              yPercent: 0,
              z: 0,
              autoAlpha: 1,
              scale: 1,
              filter: "blur(0px)",
              duration: 1,
              ease: "power2.inOut"
            }, "<") // Sync transition

            // Buffer
            .to({}, { duration: 0.1 });
        });

      }, section);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="latest-work section-spacing px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-32"
      id="latest-work"
    >
      <h2 className="latest-work__heading">Latest work</h2>

      <div className="latest-work__frame" ref={frameRef}>
        {/* Shared meta panel */}
        <div className="latest-work__meta-panel" aria-hidden>
          <div className="latest-work__meta-layer">
            <div className="latest-work__meta-top">
              <div
                className="latest-work__meta-number latest-work__roll"
                ref={metaNumberRef}
              >
                <span className="latest-work__roll-current">01.</span>
              </div>
              {renderMetaList("meta")}
            </div>
            <div className="latest-work__meta-bottom">
              <h3
                className="latest-work__meta-title latest-work__roll"
                ref={metaTitleRef}
              >
                <span className="latest-work__roll-current">Arjuna</span>
              </h3>
              <p
                className="latest-work__meta-sub latest-work__roll"
                ref={metaSubRef}
              >
                <span className="latest-work__roll-current">
                  Personal Portfolio Website for talented design engineer
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Preview layers for crossfade/stacking */}
        <div className="latest-work__preview" aria-hidden>
          <div className="latest-work__preview-layer latest-work__preview-layer--active" />
          <div className="latest-work__preview-layer latest-work__preview-layer--incoming" />
        </div>

        {projects.slice(0, 3).map((project, index) => (
          <ProjectCard
            key={project.id}
            {...project}
            index={index}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            deviceRef={(el) => {
              deviceRefs.current[index] = el;
            }}
          />
        ))}
      </div>

      <div className="latest-work__cta-row mb-12">
        <div className="item button-parrot" style={{ "--bg-color": "#2c3e50" } as React.CSSProperties}>
          <a href={routeUrl("works")} className="btn-pill latest-work__cta-button">
            More projects
            <div className="parrot" aria-hidden="true"></div>
            <div className="parrot" aria-hidden="true"></div>
            <div className="parrot" aria-hidden="true"></div>
            <div className="parrot" aria-hidden="true"></div>
            <div className="parrot" aria-hidden="true"></div>
            <div className="parrot" aria-hidden="true"></div>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LatestWork;
